import { useEffect, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2
} from 'src/mentor/helpers/utilityFunctions'
import { postCPDataWithParams } from '../../../../../clinic/services'

const DEFAULT_PDF_PREVIEW_ENDPOINT = '/api/secure/doctor/prescription/dummy/generate/pdf/preview'

// Helper function to convert base64 to Blob
function base64ToBlob(base64, contentType = 'application/pdf') {
  try {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: contentType })
  } catch (error) {
    console.error('Error converting base64 to blob:', error)
    throw error
  }
}

export default function usePdfPrintTemplate({
  prescriptionTemplateId,
  clinicId,
  useEmptyBox = false,
  apiFn = postCPDataWithParams,
  apiEndpoint = DEFAULT_PDF_PREVIEW_ENDPOINT,
  extraParams = {},
}) {
  const [pdfTemplateBlobUrl, setPdfTemplateBlobUrl] = useState()
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)
  const [filename, setFilename] = useState('prescription_preview.pdf')

  // Cleanup blob URL when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (pdfTemplateBlobUrl) {
        URL.revokeObjectURL(pdfTemplateBlobUrl)
      }
    }
  }, [pdfTemplateBlobUrl])

  // Common function to fetch PDF data from API
  const fetchPdfData = useCallback(async () => {
    const params = {
      clinicId: clinicId,
      empty: useEmptyBox,
      ...extraParams,
    }

    if (prescriptionTemplateId) {
      params.templatePrescriptionId = prescriptionTemplateId
    }

    const response = await apiFn(
      apiEndpoint,
      {},
      params,
    )

    if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const responseData = getDataMapFromCommonResponse(response)
      const { pdfData, filename: pdfFilename } = responseData

      if (!pdfData) {
        throw new Error('No PDF data received from server')
      }

      const blob = base64ToBlob(pdfData)
      return { blob, filename: pdfFilename || 'prescription_preview.pdf' }
    } else {
      throw new Error(response?.data?.message || response?.response?.data?.message || 'Failed to generate PDF')
    }
  }, [clinicId, prescriptionTemplateId, useEmptyBox, apiFn, apiEndpoint, extraParams])

  async function getPrescriptionTemplatePdf() {
    try {
      setLoading(true)
      setError(null)

      const { blob, filename: pdfFilename } = await fetchPdfData()

      // Revoke old URL if exists
      if (pdfTemplateBlobUrl) {
        URL.revokeObjectURL(pdfTemplateBlobUrl)
      }

      // Create new blob URL
      const blobUrl = URL.createObjectURL(blob)

      setPdfTemplateBlobUrl(blobUrl)
      setFilename(pdfFilename)

      return { success: true, blobUrl, filename: pdfFilename }
    } catch (err) {
      console.error('Error fetching prescription template PDF:', err)
      const errorMessage = err.message || 'Failed to generate PDF preview'
      setError(errorMessage)
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }


  async function downloadPdf() {
    try {
      setDownloading(true)

      const { blob, filename: pdfFilename } = await fetchPdfData()

      // Create blob URL for download
      const blobUrl = URL.createObjectURL(blob)

      // Create and trigger download link
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = pdfFilename
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)

      toast.success('PDF downloaded successfully')
      return { success: true }
    } catch (err) {
      console.error('Error downloading PDF:', err)
      const errorMessage = err.message || 'Failed to download PDF'
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setDownloading(false)
    }
  }

  return {
    pdfTemplateBlobUrl,
    getPrescriptionTemplatePdf,
    downloadPdf,
    fetchingPdf: loading,
    downloadingPdf: downloading,
    error,
    filename,
  }
}
